import { useEffect, useState } from "react";
import api, { formatError } from "@/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import { MessageCircle, ShieldCheck, ShieldAlert, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Per-hospital WhatsApp Business creds panel. Falls back to global env
 * credentials when this hospital hasn't set their own.
 */
export default function HospitalWhatsAppSettings() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ wa_access_token: "", wa_phone_number_id: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/org/me/whatsapp");
      setStatus(data);
      setForm({ wa_access_token: "", wa_phone_number_id: data.wa_phone_number_id || "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (form.wa_access_token) payload.wa_access_token = form.wa_access_token;
      if (form.wa_phone_number_id) payload.wa_phone_number_id = form.wa_phone_number_id;
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing to save");
        setSaving(false); return;
      }
      const { data } = await api.put("/org/me/whatsapp", payload);
      setStatus(data);
      setForm({ wa_access_token: "", wa_phone_number_id: data.wa_phone_number_id || "" });
      toast.success("WhatsApp credentials saved");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleEnabled = async (val) => {
    try {
      const { data } = await api.put("/org/me/whatsapp", { wa_enabled: val });
      setStatus(data);
      toast.success(val ? "WhatsApp enabled" : "WhatsApp muted for this hospital");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const clearCreds = async () => {
    if (!window.confirm("Remove per-hospital WhatsApp creds? Sends will fall back to platform defaults.")) return;
    try {
      const { data } = await api.put("/org/me/whatsapp", { clear: true });
      setStatus(data);
      setForm({ wa_access_token: "", wa_phone_number_id: "" });
      toast.success("Cleared. Using platform defaults.");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <Card className="medical-card p-6"><div className="text-sm text-slate-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></Card>
    );
  }

  const effective = status?.effective_configured;
  const orgOwned = status?.org_configured;

  return (
    <Card className="medical-card p-6" data-testid="hospital-whatsapp-settings">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center shrink-0">
          <MessageCircle className="w-5 h-5 text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-semibold text-slate-900">WhatsApp Queue Updates</h3>
            {effective ? (
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="wa-status-badge">
                <ShieldCheck className="w-3 h-3 mr-1" /> Live
              </Badge>
            ) : (
              <Badge className="bg-slate-100 text-slate-600 border border-slate-200" data-testid="wa-status-badge">
                <ShieldAlert className="w-3 h-3 mr-1" /> Not configured
              </Badge>
            )}
            {orgOwned && (
              <Badge className="bg-sky-50 text-sky-700 border border-sky-200">
                <KeyRound className="w-3 h-3 mr-1" /> Own credentials
              </Badge>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            Auto-notify patients about booking, &quot;your turn&quot;, follow-ups &amp; feedback over WhatsApp.
            Use your own Meta Business account for delivery + branding — or fall back to the platform default.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Enable sends</span>
          <Switch checked={!!status?.wa_enabled} onCheckedChange={toggleEnabled} data-testid="wa-enable-switch" />
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-4">
        <div>
          <Label>Phone Number ID</Label>
          <Input value={form.wa_phone_number_id}
            onChange={(e) => setForm({ ...form, wa_phone_number_id: e.target.value.trim() })}
            placeholder="e.g. 105123456789012"
            data-testid="wa-phone-id-input"
          />
          <p className="text-[11px] text-slate-500 mt-1">From Meta WABA → Phone numbers.</p>
        </div>
        <div>
          <Label>Access Token {status?.wa_access_token_last4 && (
            <span className="text-slate-400 font-normal">
              (current …{status.wa_access_token_last4})
            </span>
          )}</Label>
          <Input type="password" value={form.wa_access_token}
            onChange={(e) => setForm({ ...form, wa_access_token: e.target.value })}
            placeholder="Paste to update (write-only)"
            data-testid="wa-token-input"
          />
          <p className="text-[11px] text-slate-500 mt-1">Meta Graph long-lived token. Stored encrypted at rest.</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-2 justify-end">
        {orgOwned && (
          <Button variant="ghost" onClick={clearCreds} data-testid="wa-clear-btn"
            className="text-rose-600 hover:text-rose-700">Clear</Button>
        )}
        <Button onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="wa-save-btn">
          {saving ? "Saving…" : "Save credentials"}
        </Button>
      </div>

      {!effective && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-xs p-3" data-testid="wa-fallback-notice">
          WhatsApp is currently <b>not configured</b>. Patient queue updates will fall back to SMS only until you connect a WABA (or the platform default becomes available).
        </div>
      )}
    </Card>
  );
}
